import hyperdom from 'hyperdom'
import max from 'lowscore/max.js'
import MultiCityValidationRules from './multiCityValidationRules.js'
import autocomplete from '../autocomplete.jsx'
import datePicker from '../datePicker.js'
import timePicker from '../timePicker.jsx'
import tripNotes from '../tripNotes.jsx'
import datesNotes from '../flights/datesNotes.jsx'
import * as gtmDataLayer from '../analytics/gtm/dataLayer.js'
import FlightPreferences from './flightPreferences.jsx'
import {calculateLegs} from './tropicsLeg.js'


export default class MultiCityCriteria {
  constructor (model) {
    this.model = model
    this.flightPreferences = new FlightPreferences(model)
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  search () {
    const model = this.model
    const {exploreFlights, flights} = model
    const multiCityFlights = exploreFlights.multiCity
    flights.clearWarnings()

    const operations = {
      enabled: () => {
        model.showLoading()
        const criteria = {
          cabin: exploreFlights.cabin === 'any' ? undefined : exploreFlights.cabin,
          airlines: exploreFlights.airline === 'any' ? [] : [flights.criteria.model.exploreFlights.airline],
          segments: multiCityFlights.map((flight, index) => {
            let departDate
            if(index === 0) {
              departDate = exploreFlights.getDepartDate(flight.departureDate, flight.departTime)
            } else if(index === multiCityFlights.length - 1){
              departDate = exploreFlights.getReturnDate(flight.departureDate, flight.departTime)
            } else {
              departDate = exploreFlights.getDepartDate(flight.departureDate, flight.departTime, '00:00')
            }
            return {
              arrive: flight.arriveAirport.slice(-3),
              depart: flight.departureAirport.slice(-3),
              departDate: departDate,
            }
          }),
        }

        return model.api.searchFlights({
          criteria,
          departureCode: model.departure.code,
          tourCode: model.departure.tourCode,
          sellingCompanyCode: model.departure.sellingCompanyCode,
          party: this.model.party(),
        }).then(({results}) => {
          flights.searchResults = calculateLegs(results)
          if (flights.searchResults.length === 0) {
            flights.searchMode = 'noResults'
          } else {
            flights.searchMode = 'results'
          }
          model.hideLoading()
          this.refresh()
        }).catch((e) => {
          console.error(e)
          model.hideLoading()
          flights.searchMode = 'notAvailable'
          this.refresh()
        })
      },
      callback: () => {
        model.showLoading()
        setTimeout(() => { // just making this look like it is doing something
          flights.searchMode = 'notAvailable'
          model.hideLoading()
          this.refresh()
        }, 300)
      },
    }

    if (this.validate()) {
      operations[model.config.flights.mode]()
    }
  }

  validate () {
    const multiCityFlights = this.model.exploreFlights.multiCity
    let index = 0
    const flightsValidationResults = multiCityFlights.map((flight) => {
      var validator = new MultiCityValidationRules(this.model, flight, index === multiCityFlights.length - 1)
      index++
      return validator.validate()
    })
    return !flightsValidationResults.some(x => !x)
  }

  getError (model, id) {
    return (model.validationErrors || {})[id]
  }

  renderErrors (model, id){
    var error = this.getError(model, id)
    if (error) {
      return <div class="o-form-item__warning">
        { error }
      </div>
    }
  }
  render () {
    var model = this.model
    var getError = this.getError
    var flights = model.flights
    var exploreFlights = model.exploreFlights
    var legs = exploreFlights.multiCity = (exploreFlights.multiCity || [{id: 1}, {id: 2}, {id: 3}])
    var dateFormat = model.config.dateFormat

    function addFlight (){
      var nextId = max(legs.map(flight => {return flight.id})) + 1
      legs.push({
        id: nextId,
      })
    }

    function removeFlight (flight){
      var flightIndex = legs.indexOf(flight)
      legs.splice(flightIndex, 1)
    }

    function renderRemoveFlight (flight){
      if (flight.id > 2) {
        return <a class="flights__title-remove" onclick={() => {removeFlight(flight)}}>Remove</a>
      }
    }

    function renderFlight (flight, index){
      var flightCount = legs.indexOf(flight)+1
      var isFirstDeparture = legs.indexOf(flight) === 0
      var isLastDeparture = legs.indexOf(flight) === legs.length - 1
      var validator = new MultiCityValidationRules(model, flight)
      var departureAirports = isFirstDeparture ? model.homeCountryAirports : model.allAirports
      var arriveAirports = isLastDeparture ? model.homeCountryAirports : model.allAirports
      const showTripNotes = model.config.flights.requireTripNotesRead && this.model.departure.notes && this.model.departure.notes.length
      if ((isLastDeparture || getError(flight, 'arriveAirport')) && flight.arriveAirport) {
        validator.rules.arriveAirport()
      }
      if (!model.departure.notes || model.departure.notes.length === 0) {
        flight.tripNotesRead = true
      }
      return <div class="flights__flight">
        <div className='flights__title-container'>
          <h3 class="flights__flight-title">Flight {flightCount}</h3>
          {renderRemoveFlight(flight)}
        </div>
        <div className='flights__airports-container'>
          <div class ="flights-search__form">
            <div className="o-form-item flights__airports-departure">
              <label for={"departureAirport"+flight.id} className="o-form-item__label flights__label" key="departure-airport">
                Flying from
              </label>
              <i className="o-form-item__icon -new-ui_pin"></i>
              {autocomplete({
                id: "departureAirport"+flight.id,
                placeholder: "City name or airport name",
                binding: [flight, 'departureAirport'],
                suggestions: departureAirports,
                validate: validator.rules.departureAirport,
                onkeydown: this.searchIfEnter,
                disableDebounce: this.model.disableLoadingTimeout,
              })}
              {this.renderErrors(flight, 'departureAirport')}
            </div>
            <div className='flights__airports-icon-between'></div>
            <div className="o-form-item flights__airports-arrive multi-city">
              <label htmlFor="arriveAirport" className="o-form-item__label flights__label" key="arrive-airport">
                Flying to*
              </label>
              <i className="o-form-item__icon multi-city -new-ui_pin"></i>
              {autocomplete(
                {
                  id: "arriveAirport"+flight.id,
                  placeholder: "City name or airport name",
                  binding: [flight, 'arriveAirport'],
                  suggestions: arriveAirports,
                  validate: validator.rules.arriveAirport,
                  onkeydown: this.searchIfEnter,
                  disableDebounce: this.model.disableLoadingTimeout,
                }
              )}
              {this.renderErrors(flight, 'arriveAirport')}
            </div>
          </div>
        </div>
        <div className='flights__dates-container'>
          <div className="o-form-item flights__dates-departure multi-city">
            <label for="departureDate" className="o-form-item__label flights__label" key="departure-date">
              Flight dates
            </label>
            <i className="o-form-item__icon -new-ui_calendar"></i>
            {datePicker(
              <input className={'o-form-item__txt-input '}
                type="text"
                id="departureDate"
                readOnly
                onblur={validator.rules.departureDate}
                onkeydown={this.searchIfEnter}
                placeholder='Departure date'
              />,
              {
                binding: [flight, 'departureDate'],
                format: dateFormat,
                parseFormat: dateFormat,
                defaultDate: flights.defaultDepartDate,
              }
            )}
            <div className="o-form-item u-inbl u-margin-left-small form-item-departure-time">
              {timePicker({binding: [flight, 'departTime']})}
            </div>
            {this.renderErrors(flight, 'departureDate')}
          </div>
        </div>
        {
          (index === legs.length - 1 && showTripNotes) ?
            <div class="flights__trip-notes" onmouseleave={() => flights.showTripNotes = false}>
              <label class="o-choice-item -fullWidth">
                <input onclick={validator.rules.tripNotesRead} binding={[flight, 'tripNotesRead']} type="checkbox" class="o-choice-item__checkbox" name="termsAndConditions" id="termsAndConditions"/>
                <div class="o-choice-item__content">
                  I have read the <a href="#" onclick={(ev) => {ev.preventDefault();flights.showTripNotes = !flights.showTripNotes}} class="trip-notes-open">trip notes</a>
                </div>
              </label>
              {this.renderErrors(flight, 'tripNotesRead')}
              <div class={['help-content', {'trip-notes' : flights.showTripNotes}]}>
                { tripNotes(this.model.departure.notes) }
              </div>
            </div>
            : undefined
        }

      </div>
    }
    return <div className='u-padding-top-none flights__search-multi-city-criteria'>
      {this.flightPreferences}
      {datesNotes(model)}
      <div>
        {legs.map(renderFlight.bind(this))}
      </div>
      <div className="flights__button">
        <button type="button" class="btn-secondary-outline multi-city" onclick={addFlight}>
          <i className='-new-ui_plus'/>Add flight
        </button>
        <button type="button" onclick={this.search.bind(this)} className="btn-secondary">
          Find Flights
        </button>
      </div>
    </div>
  }
}
